import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import CampaignIcon from '@mui/icons-material/Campaign';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import {
  ORDER_STATUS_ABANDONED,
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_DECLINED,
  ORDER_STATUS_FAILED,
} from '~/consts/order';

import { useGenerateAdminEsimPromo } from '~/hooks/apis/loyalty/useGenerateAdminEsimPromo';
import { useGetLoyaltyAccount } from '~/hooks/apis/loyalty/useGetLoyaltyAccount';
import { useGetOrder } from '~/hooks/apis/orders/useGetOrder';

import ApiError from '~/types/ApiError';
import type { AdminEsimGenerateResult } from '~/types/loyaltyContract';

import isUUID from '~/utils/isUUID';

import SocieteEsimOrderSummaryCard from './SocieteEsimOrderSummaryCard';
import { buildSocieteEsimOrderSummaryModel } from './societeEsimOrderSummaryModel';

/** Caps the two-column workspace + step 3 so the bottom panel matches the top row, not full-bleed desktop. */
const ESIM_WORKSPACE_MAX_WIDTH_PX = 1040;

/** Order statuses that disqualify an order from generating an eSIM benefit. */
const NON_GENERATABLE_ORDER_STATUSES = new Set<string>([
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_ABANDONED,
  ORDER_STATUS_FAILED,
  ORDER_STATUS_DECLINED,
]);

const MONOSPACE_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

/** UUID inputs: monospace so hyphenated ids align and read clearly at a given field width. */
const uuidTextFieldSx = {
  '& .MuiOutlinedInput-input': {
    fontFamily: MONOSPACE_FONT_FAMILY,
  },
} as const;

export default function SocieteEsimGeneratorPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [orderId, setOrderId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [lastResult, setLastResult] = useState<AdminEsimGenerateResult | null>(null);
  const [promoGeneratedAt, setPromoGeneratedAt] = useState<Date | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const theme = useTheme();

  const deferredOrderId = useDeferredValue(orderId.trim());
  const orderQueryEnabled = isUUID(deferredOrderId);

  const lastHydratedOrderId = useRef<string | null>(null);

  const {
    data: orderPayload,
    isLoading: isOrderLoading,
    isError: isOrderError,
    error: orderError,
  } = useGetOrder(deferredOrderId, orderQueryEnabled);

  const order = orderPayload?.result as App.Order | undefined;

  const { data: loyaltyPayload } = useGetLoyaltyAccount(order?.fk_customer_id, orderQueryEnabled);

  const loyaltyTier = loyaltyPayload?.result?.accountSummary?.tier ?? null;

  const { orderErrorMessage, orderErrorSeverity } = useMemo(() => {
    if (!isOrderError || !orderQueryEnabled) {
      return { orderErrorMessage: null as string | null, orderErrorSeverity: 'error' as const };
    }
    const err = orderError;
    if (err instanceof ApiError) {
      if (err.status === 404) {
        return {
          orderErrorMessage:
            'No order was returned for this ID (404). The order may not exist in this environment, or your admin API host may not reach the same orders service. The customer ID is filled from the order when it loads.',
          orderErrorSeverity: 'warning' as const,
        };
      }
      if (err.status === 403) {
        return {
          orderErrorMessage:
            'Forbidden (403): your account may not be allowed to read this order. The customer ID is filled from the order when it loads.',
          orderErrorSeverity: 'warning' as const,
        };
      }
      return { orderErrorMessage: err.message, orderErrorSeverity: 'error' as const };
    }
    if (err instanceof Error) {
      return { orderErrorMessage: err.message, orderErrorSeverity: 'error' as const };
    }
    return { orderErrorMessage: 'Could not load order', orderErrorSeverity: 'error' as const };
  }, [isOrderError, orderError, orderQueryEnabled]);

  useEffect(() => {
    if (!orderQueryEnabled) {
      lastHydratedOrderId.current = null;
      setCustomerId('');
      return;
    }
    if (isOrderError) {
      lastHydratedOrderId.current = null;
      setCustomerId('');
    }
  }, [orderQueryEnabled, isOrderError]);

  useEffect(() => {
    if (!order) {
      return;
    }
    if (lastHydratedOrderId.current === order.id) {
      return;
    }
    setCustomerId(order.fk_customer_id);
    lastHydratedOrderId.current = order.id;
  }, [order]);

  const summaryModel = useMemo(() => {
    if (!order) {
      return null;
    }
    return buildSocieteEsimOrderSummaryModel(order, {
      loyaltyAccountTier: loyaltyTier,
    });
  }, [order, loyaltyTier]);

  const mutation = useGenerateAdminEsimPromo();

  const orderStatus = order?.status?.toLowerCase() ?? null;
  const isOrderBlocked = orderStatus != null && NON_GENERATABLE_ORDER_STATUSES.has(orderStatus);

  // Submit gate: order must be loaded *and* belong to the order id the user currently has typed.
  // Without this, a user pastes UUID-A → order A loads (customerId=A) → edits to UUID-B (still UUID,
  // query refetching) → clicks Generate, sending {orderId: B, customerId: A's customer} mid-load.
  const trimmedOrderId = orderId.trim();
  const isHydratedForCurrentInput =
    !!order && order.id === trimmedOrderId && lastHydratedOrderId.current === trimmedOrderId;

  const departureIso = summaryModel?.departureIso ?? null;
  const hasDerivableDeparture = !!departureIso;

  const canSubmit =
    isHydratedForCurrentInput &&
    customerId.trim().length > 0 &&
    !isOrderBlocked &&
    hasDerivableDeparture &&
    !mutation.isPending;

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) {
        return;
      }
      setConfirmOpen(true);
    },
    [canSubmit],
  );

  const confirmAndGenerate = useCallback(() => {
    if (!canSubmit || !departureIso) {
      return;
    }
    mutation.mutate(
      {
        orderId: orderId.trim(),
        customerId: customerId.trim(),
        departureDate: departureIso,
      },
      {
        onSuccess: (res) => {
          setLastResult(res.result);
          setPromoGeneratedAt(new Date());
          setConfirmOpen(false);
          enqueueSnackbar(res.message || 'eSIM promocode generated', { variant: 'success' });
        },
        onError: (err: unknown) => {
          setLastResult(null);
          setPromoGeneratedAt(null);
          setConfirmOpen(false);
          const message = err instanceof ApiError ? err.message : 'Request failed';
          enqueueSnackbar(message, { variant: 'error' });
        },
      },
    );
  }, [canSubmit, customerId, departureIso, enqueueSnackbar, mutation, orderId]);

  const copyPromocode = useCallback(() => {
    if (!lastResult?.promocode) {
      return;
    }
    void navigator.clipboard.writeText(lastResult.promocode);
    enqueueSnackbar('Promocode copied', { variant: 'info' });
  }, [enqueueSnackbar, lastResult?.promocode]);

  const accent = theme.palette.primary.main;
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const generatedStamp = promoGeneratedAt
    ? dayjs(promoGeneratedAt).tz('Australia/Sydney').format('D MMM YYYY · h:mm A')
    : null;

  return (
    <Box>
      <Alert icon={<CampaignIcon />} severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Generate eSIM promo codes</AlertTitle>
        Generates an eSIM promo code for customers on orders that did not already have one, or when support needs to
        create or re-issue the benefit from this page instead of the usual customer flow.
      </Alert>

      <Stack spacing={3}>
        {orderErrorMessage && <Alert severity={orderErrorSeverity}>{orderErrorMessage}</Alert>}

        {isOrderBlocked && (
          <Alert severity="warning">
            This order is <strong>{orderStatus}</strong> and is not eligible for an eSIM promo. Generate is disabled
            until a different, valid order is loaded.
          </Alert>
        )}

        {isHydratedForCurrentInput && !isOrderBlocked && !hasDerivableDeparture && (
          <Alert severity="warning">
            Could not derive a departure date from this order&apos;s items (no hotel check-in, tour/cruise start, or
            experience booking date found). Generate is disabled until the order has a parseable travel date.
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: ESIM_WORKSPACE_MAX_WIDTH_PX },
            alignSelf: 'flex-start',
          }}
        >
          <Stack spacing={3}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: 'stretch',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  flex: { md: '1 1 0%' },
                  minWidth: 0,
                  width: { xs: '100%', md: 'auto' },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    borderRadius: 2,
                    border: `1px solid ${borderSoft}`,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.75,
                      py: 1,
                      borderBottom: `1px solid ${borderSoft}`,
                      bgcolor: alpha(accent, 0.06),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                      Generate eSIM
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, textAlign: 'right', flexShrink: 0 }}
                    >
                      Step 1
                    </Typography>
                  </Box>
                  <Box sx={{ px: 1.75, py: 1.25, flexShrink: 0 }}>
                    <Stack spacing={2} component="form" id="esim-generator-form" onSubmit={onSubmit}>
                      <TextField
                        label="Order ID"
                        value={orderId}
                        onChange={(ev) => setOrderId(ev.target.value)}
                        required
                        fullWidth
                        autoComplete="off"
                        disabled={confirmOpen || mutation.isPending}
                        sx={uuidTextFieldSx}
                      />
                      <TextField
                        label="Customer ID"
                        value={customerId}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ readOnly: true }}
                        inputProps={{
                          'aria-label': 'Customer ID (populated from the order when it loads)',
                        }}
                        sx={uuidTextFieldSx}
                        helperText="Populated from the order"
                      />
                      <Button type="submit" variant="contained" disabled={!canSubmit} sx={{ alignSelf: 'flex-start' }}>
                        {mutation.isPending ? 'Generating…' : 'Generate promocode'}
                      </Button>
                    </Stack>
                  </Box>
                </Paper>
              </Box>

              <Box
                sx={{
                  flex: { md: '1 1 0%' },
                  minWidth: 0,
                  width: { xs: '100%', md: 'auto' },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <SocieteEsimOrderSummaryCard
                  orderQueryActive={orderQueryEnabled}
                  isLoading={orderQueryEnabled && isOrderLoading}
                  model={summaryModel}
                />
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                width: '100%',
                borderRadius: 2,
                border: `1px solid ${borderSoft}`,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  px: 1.75,
                  py: 1,
                  borderBottom: `1px solid ${borderSoft}`,
                  bgcolor: alpha(accent, 0.06),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  eSIM promo code
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textAlign: 'right', flexShrink: 0 }}
                >
                  Step 3
                </Typography>
              </Box>
              <Box sx={{ px: 1.75, py: 1.25 }}>
                {lastResult?.promocode ? (
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                      <Chip label="Generated" color="success" size="small" sx={{ fontWeight: 700 }} />
                      {generatedStamp ? (
                        <Typography variant="caption" color="text.secondary">
                          {generatedStamp}
                        </Typography>
                      ) : null}
                    </Stack>
                    <Box
                      sx={{
                        bgcolor: alpha(accent, 0.08),
                        color: 'text.primary',
                        border: `1px solid ${alpha(accent, 0.35)}`,
                        borderRadius: 1,
                        px: 2,
                        py: 1.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      }}
                    >
                      <Typography
                        component="code"
                        sx={{
                          fontFamily: MONOSPACE_FONT_FAMILY,
                          fontSize: { xs: '1.125rem', sm: '1.35rem' },
                          fontWeight: 600,
                          wordBreak: 'break-all',
                          lineHeight: 1.35,
                          flex: 1,
                          minWidth: 0,
                          color: 'text.primary',
                        }}
                      >
                        {lastResult.promocode}
                      </Typography>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        size="medium"
                        startIcon={<ContentCopyIcon />}
                        onClick={copyPromocode}
                        sx={{ flexShrink: 0 }}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Stack direction="row" flexWrap="wrap" sx={{ pt: 0.25, gap: 1, columnGap: 2, rowGap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Order · {lastResult.orderId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Package · {lastResult.packageSize}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tier · {lastResult.tier}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Partner · {lastResult.partner}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      minHeight: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2,
                      py: 2,
                      borderRadius: 1,
                      border: `1px dashed ${alpha(theme.palette.divider, 0.45)}`,
                      bgcolor: alpha(theme.palette.divider, 0.04),
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No promo code yet. Complete step 1, review the order summary, then choose Generate promocode.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!mutation.isPending) {
            setConfirmOpen(false);
          }
        }}
        aria-labelledby="esim-confirm-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="esim-confirm-title">Generate eSIM promo code?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              An eSIM promo code will be issued to this customer. Confirm the details below.
            </Typography>
            <Divider />
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}
              >
                Customer
              </Typography>
              <Typography variant="body2">{summaryModel?.customerName ?? '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {summaryModel?.customerEmail ?? '—'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: MONOSPACE_FONT_FAMILY, color: 'text.secondary', wordBreak: 'break-all' }}
              >
                {summaryModel?.customerIdFull ?? '—'}
              </Typography>
            </Stack>
            <Divider />
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}
              >
                Order
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: MONOSPACE_FONT_FAMILY, wordBreak: 'break-all' }}>
                {trimmedOrderId}
              </Typography>
              {summaryModel?.bookingLine && summaryModel.bookingLine !== '—' ? (
                <Typography variant="body2" color="text.secondary">
                  {summaryModel.bookingLine}
                </Typography>
              ) : null}
              {summaryModel?.departureDisplay && summaryModel.departureDisplay !== '—' ? (
                <Typography variant="body2" color="text.secondary">
                  Departure · {summaryModel.departureDisplay}
                </Typography>
              ) : null}
            </Stack>
            {summaryModel?.tierBadgeLabel || summaryModel?.packageLine ? (
              <>
                <Divider />
                <Stack direction="row" spacing={2}>
                  {summaryModel.tierBadgeLabel ? (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}
                      >
                        Tier
                      </Typography>
                      <Typography variant="body2">{summaryModel.tierBadgeLabel}</Typography>
                    </Box>
                  ) : null}
                  {summaryModel.packageLine && summaryModel.packageLine !== '—' ? (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}
                      >
                        Package
                      </Typography>
                      <Typography variant="body2">{summaryModel.packageLine}</Typography>
                    </Box>
                  ) : null}
                </Stack>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={confirmAndGenerate} variant="contained" color="primary" disabled={!canSubmit}>
            {mutation.isPending ? 'Generating…' : 'Confirm & generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
